import * as LocalStore from "./LocalStore";

import ons from "onsenui";
import { APIConfig } from "./API";
import { APIConfigBookmark } from "./LocalStore";

/**
 * opens a modal window to select api configurations, and returns them
 * @returns {Promise<APIConfig | null>}
 */
export async function selectApiConfig(
  handleAuthTokenLogin: (authToken: string, apiConfig: APIConfig) => void
): Promise<APIConfig | null> {
  const bookmarks = await LocalStore.getAPIBookmarks();

  const buttons = ["Production", "Staging", "Local"]
    .concat(bookmarks.map((b) => b.title))
    .concat(["(authToken)", "(Reset)", "(Add)"]);
  const authTokenButtonIndex = buttons.length - 3;
  const resetButtonIndex = buttons.length - 2;
  const addButtonIndex = buttons.length - 1;

  const apiConfig = await LocalStore.loadAPIConfig();

  const selection = await ons.notification
    .alert({
      animation: "none",
      modifier: "material",
      title: "Select API Server",
      message:
        "apiBaseUrl: " +
        apiConfig.apiBaseUrl +
        " " +
        "socketIOServerUrl: " +
        apiConfig.socketIOServerUrl,
      buttonLabels: <any>buttons,
      cancelable: true,
    })
    .then(
      async (result): Promise<APIConfig | null> => {
        const buttonIndex: number = <any>result;
        if (buttonIndex === 0) {
          return LocalStore.productionApiConfig;
        } else if (buttonIndex === 1) {
          return LocalStore.stagingApiConfig;
        } else if (buttonIndex === 2) {
          return LocalStore.localApiConfig;
        } else if (buttonIndex === authTokenButtonIndex) {
          const authToken: string = <any>await ons.notification.prompt(" ", {
            animation: "none",
            modifier: "material",
            cancelable: true,
            title: "Enter authToken",
          });

          handleAuthTokenLogin(authToken, apiConfig);
          return apiConfig;
        } else if (buttonIndex === resetButtonIndex) {
          await LocalStore.clearAPIBookmarks();
          return selectApiConfig((authToken: string, aconfig: APIConfig) =>
            handleAuthTokenLogin(authToken, aconfig)
          );
        } else if (buttonIndex === addButtonIndex) {
          const bookmarkToAdd = await createBookmarkToAdd();
          if (bookmarkToAdd !== null) {
            await LocalStore.addAPIBookmark(bookmarkToAdd);
            return bookmarkToAdd.apiConfig;
          } else {
            return selectApiConfig((authToken: string, aconfig: APIConfig) =>
              handleAuthTokenLogin(authToken, aconfig)
            );
          }
        } else {
          // If cancelled return to prod
          if (buttonIndex === -1) {
            return LocalStore.productionApiConfig;
          }
          return bookmarks[buttonIndex - 3].apiConfig;
        }
      },
      () => {
        // ERROR revert to prod
        return LocalStore.productionApiConfig;
      }
    );

  return selection;
}

export async function createBookmarkToAdd(): Promise<APIConfigBookmark | null> {
  try {
    const title: string = <any>await ons.notification.prompt(" ", {
      animation: "none",
      modifier: "material",
      cancelable: true,
      title: "Enter bookmark title",
    });

    const apiUrl: string = <any>await ons.notification.prompt(" ", {
      animation: "none",
      modifier: "material",
      cancelable: true,
      title: "Enter API Url",
    });
    if (!(apiUrl.substr(0, 7) === "http://" || apiUrl.substr(0, 8) === "https://")) {
      await ons.notification.alert('URL must start with "http://" or "https://"', {
        animation: "none",
        modifier: "material",
      });
      return createBookmarkToAdd();
    }
    const socketIOUrl: string = <any>await ons.notification.prompt(" ", {
      animation: "none",
      modifier: "material",
      cancelable: true,
      title: "Enter Socket.io Url",
    });
    if (!(socketIOUrl.substr(0, 7) === "http://" || socketIOUrl.substr(0, 8) === "https://")) {
      await ons.notification.alert('URL must start with "http://" or "https://"', {
        animation: "none",
        modifier: "material",
      });
      return createBookmarkToAdd();
    }

    const validApiUrl =
      apiUrl.charAt(apiUrl.length - 1) === "/" ? apiUrl.substr(0, apiUrl.length - 1) : apiUrl;
    const validSocketIOUrl =
      socketIOUrl.charAt(socketIOUrl.length - 1) === "/"
        ? socketIOUrl.substr(0, socketIOUrl.length - 1)
        : socketIOUrl;

    return {
      title: title,
      apiConfig: {
        apiBaseUrl: validApiUrl,
        socketIOServerUrl: validSocketIOUrl,
      },
    };
  } catch (e) {
    // Dialog was canceled

    console.log(e.message);

    return null;
  }
}
